<template>
  <div class="flex">
    <div class="mt-1">
      <div :class="[{ 'text-xs ml-4 relative top-2': labelStyle == 2 }]" class="text-common-grey whitespace-nowrap">
        {{ label }}
      </div>
      <label v-if="description" class="block text-sm font-normal text-gray-500 dark:text-white">
        {{ description }}
      </label>
      <div class="flex mt-2">
        <div id="selected-contacts-wrapper" class="flex flex-wrap gap-x-2">
          <div v-for="(user, i) in selected_contacts" :key="user.id" class="relative">
            <Avatar
              :hover-effect="true"
              :first-name="Record.from(user).first_name"
              :last-name="Record.from(user).last_name"
              :image="Record.from(user).avatar"
              :email="Record.from(user).email"
              :show-tooltip="true"
              :no-valid-email-effect="true" />
            <div
              v-if="!disabled"
              id="remove-user-button"
              class="absolute w-4 h-4 text-xs text-red-400 border rounded-full cursor-pointer bg-light-screen-background-grey dark:bg-dark-screen-background dark:border-dark-divider-border z-1 -top-2 -right-1"
              @click="handleRemoveUser(i)">
              <span class="ml-[4px] relative bottom-[2px]">x</span>
            </div>
          </div>
        </div>

        <div
          v-if="!disabled && !(selected_contacts.length == 1 && !multiSelect)"
          class="justify-center mr-2 text-center align-middle cursor-pointer dark:border-dark-divider-border h-9 w-9"
          :class="[selected_contacts.length ? 'ml-2' : '']">
          <div
            id="plus-icon"
            class="w-8 h-8 border border-dotted rounded-full dark:border-dark-divider-border"
            @click="
              () => {
                add_user_mode = !add_user_mode
                fetchContacts('')
              }
            ">
            <Plus v-if="!disabled" class="w-5 h-5 text-common-grey mt-[5px] ml-[5px]" />
          </div>
          <div v-if="add_user_mode" class="w-full mt-2 min-h-[50px] min-w-[250px]">
            <UserandContactsAutoCompleteInput
              :label="label"
              :input-labels="inputLabels"
              :is-box-background="true"
              :contacts="contacts_list"
              :companies="companies"
              :excluded-contacts="excludedContacts"
              :selected-contacts="selected_contacts"
              :disabled="disabled"
              :users="[]"
              value-attr="full_name"
              :loading="loading"
              @changed="handleChange"
              @focusout="
                () => {
                  $utils.debounce(() => {
                    add_user_mode = false
                  }, 200)
                }
              "
              @reset="add_user_mode = false"
              @chosen="handleAddUser($event)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dataService from "@/services/data.service"
import { Plus } from "lucide-vue-next"
import UserandContactsAutoCompleteInput from "@/components/shared/UserandContactsAutoCompleteInput.vue"

import Avatar from "@/components/shared/Avatar.vue"
import { utils } from "@/store/helpers"
import { Record } from "@/models/record"

export default {
  components: { Plus, UserandContactsAutoCompleteInput, Avatar },
  props: {
    excludedContacts: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      required: false
    },
    inputLabels: {
      type: Array,
      required: false
    },

    companies: {
      type: Array,
      required: false
    },
    selectedContacts: {
      type: Array,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    labelStyle: {
      type: Number,
      default: 1
    },
    multiSelect: {
      type: Boolean,
      default: true
    },
    description: {
      type: String,
      default: ""
    }
  },
  emits: ["done", "changed", "update:selectedContacts"],
  data() {
    return {
      Record,
      change_timeout: null,
      add_user_mode: false,
      selected_contacts: [],
      contacts_list: [],
      loading: false,
      old_companies: []
    }
  },
  computed: {},
  watch: {
    companies: {
      deep: true,
      handler() {
        const companies_cleaned = this.companies.filter((c) => c)
        if (this.old_companies.length !== companies_cleaned.length) {
          // this.fetchContacts("")
          if (this.old_companies.length > companies_cleaned.length) {
            let removed_company = null
            this.old_companies.forEach((company) => {
              if (!companies_cleaned.map((c) => c.id).includes(company.id)) {
                removed_company = company
              }
            })
            this.removeContactsforCompany(removed_company)
          }
          this.old_companies = [...companies_cleaned]
        }
      }
    },
    selectedContacts: {
      deep: true,
      handler() {
        this.selected_contacts = this.selectedContacts.map((item) =>
          item.id
            ? item
            : this.$store.state.data.users.find((o_u) => o_u.id == item)
              ? this.$store.state.data.users.find((o_u) => o_u.id == item)
              : item
        )
      }
    }
  },
  created() {
    if (this.selectedContacts?.length) {
      this.selected_contacts = this.selectedContacts
    }
  },
  methods: {
    fetchContacts(string) {
      this.contacts_list = []
      if (this.companies?.length > 0) {
        this.loading = true
        let companies_ids = null
        if (this?.companies.length) {
          companies_ids = this.companies.map((c) => (c?.id ? c.id : c))
        }

        companies_ids.forEach((company_id, i) => {
          dataService
            .getRecordChildren(company_id, { ordering: "-name", name_query: string, object_type: "contact" })
            .then((result) => {
              if (i == companies_ids.length - 1) {
                this.loading = false
              }
              result.data.results.forEach((user) => {
                this.contacts_list.push(user)
                this.contacts_list = utils.uniqBy(this.contacts_list, "id")
              })
            })
            .catch(() => {
              if (i == companies_ids.length - 1) {
                this.loading = false
              }
            })
        })
      } else {
        this.loading = true
        dataService.getFilteredContacts(string).then((result) => {
          this.loading = false
          result.data.results.forEach((user) => {
            this.contacts_list.push(user)
            this.contacts_list = utils.uniqBy(this.contacts_list, "id")
          })
        })
      }
    },

    handleChange(string) {
      clearTimeout(this.change_timeout)
      this.change_timeout = setTimeout(() => {
        this.fetchContacts(string)
      }, 500)
    },

    handleAddUser(e) {
      this.selected_contacts.push(e)
      this.add_user_mode = false
      this.$emit("update:selectedContacts", this.selected_contacts)
    },

    handleRemoveUser(i) {
      this.selected_contacts.splice(i, 1)
      this.$emit("update:selectedContacts", this.selected_contacts)
    },

    removeContactsforCompany(company) {
      const new_contacts_list = []
      this.selected_contacts.forEach((contact) => {
        if (contact.parent !== company.id) {
          new_contacts_list.push(contact)
        }
      })
      this.selected_contacts = [...new_contacts_list]
    }
  }
}
</script>
